import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, Text, useWindowDimensions, View } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { useIsFocused } from "@react-navigation/native";
import CloseIcon from "@/assets/svgs/close-icon";
import { Button } from "../ui/button";
import useSlotSelector, { TimeSlot } from "@/elements/hooks/use-slot-selector";
import CheckIcon from "@/assets/svgs/check-icon";
import { api } from "../utils/convex-api";
import { Id } from "@teachloop/backend/_generated/dataModel";
import AddIcon from "@/assets/svgs/add-icon";
import MinusIcon from "@/assets/svgs/minus-icon";

export type AssignTimeSlotSheetPayload = {
  preSelectedTimeSlot?: {
    start: string;
    end: string;
    slotId: Id<"tutorTimeSlots">;
  };
  day: (typeof api.tutor.profileSettings.queries.getAllAvailabilityDays._returnType)[number];
};

export type AssignTimeSlotSheetResult = {
  selectedTimeSlot?: {
    start: string;
    end: string;
  };
  slotId?: Id<"tutorTimeSlots">;
};

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

function AssignTimeSlotSheetContent({
  timeSlots,
  onClose,
  onDone,
  onRemove,
  preSelectedTimeSlot,
}: {
  preSelectedTimeSlot?: AssignTimeSlotSheetPayload["preSelectedTimeSlot"];
  timeSlots: TimeSlot[];
  onClose: () => void;
  onRemove: () => void;
  onDone: (slotStartTime: string, slotEndTime: string) => void;
}) {
  const {
    slotStartTime,
    slotEndTime,
    nextStartTimeSlot,
    prevStartTimeSlot,
    nextEndTimeSlot,
    prevEndTimeSlot,
    setSlotStartTime,
    setSlotEndTime,
    canGoNextStart,
    canGoPrevStart,
    canGoNextEnd,
    canGoPrevEnd,
  } = useSlotSelector("09:00", "21:00", 60, 15, 60, 480, timeSlots);

  useEffect(() => {
    if (preSelectedTimeSlot) {
      setSlotStartTime(preSelectedTimeSlot.start);
      setSlotEndTime(preSelectedTimeSlot.end);
    }
  }, [preSelectedTimeSlot, setSlotStartTime, setSlotEndTime]);

  return (
    <View className="bg-teachLoop-creamBase rounded-t-[20]">
      <View className="h-6" />
      <View className="mx-5 flex-row items-center">
        <Text className="text-xl font-geologica-medium font-medium text-black flex-1">
          Enter a time slot
        </Text>
        <Pressable onPress={onClose} className="active:opacity-80">
          <CloseIcon
            className="size-6"
            pathClassName="stroke-teachLoop-textPrimary"
          />
        </Pressable>
      </View>
      <View className="h-9" />
      <View className="mx-5 gap-6">
        <View className="gap-4">
          <Text className="text-base font-geologica-medium font-medium text-black text-center">
            Starting
          </Text>

          <View className="flex-row items-center justify-center gap-6">
            <Pressable onPress={prevStartTimeSlot} disabled={!canGoPrevStart} style={canGoPrevStart ? { opacity: 1 } : { opacity: 0.25 }}>
              <MinusIcon className="size-6" pathClassName="stroke-teachLoop-textPrimary" />
            </Pressable>
            <Text className="text-4xl font-geologica-medium font-medium text-black text-center w-[150px]">
              {slotStartTime}
            </Text>
            <Pressable onPress={nextStartTimeSlot} disabled={!canGoNextStart} style={canGoNextStart ? { opacity: 1 } : { opacity: 0.25 }}>
              <AddIcon className="size-6" pathClassName="stroke-teachLoop-textPrimary" />
            </Pressable>
          </View>
        </View>

        <View className="h-[1px] bg-black opacity-10" />

        <View className="gap-4">
          <Text className="text-base font-geologica-medium font-medium text-black text-center">
            Ending
          </Text>

          <View className="flex-row items-center justify-center gap-6">
            <Pressable onPress={prevEndTimeSlot} disabled={!canGoPrevEnd} style={canGoPrevEnd ? { opacity: 1 } : { opacity: 0.25 }}>
              <MinusIcon className="size-6" pathClassName="stroke-teachLoop-textPrimary" />
            </Pressable>
            <Text className="text-4xl font-geologica-medium font-medium text-black text-center w-[150px]">
              {slotEndTime}
            </Text>
            <Pressable onPress={nextEndTimeSlot} disabled={!canGoNextEnd} style={canGoNextEnd ? { opacity: 1 } : { opacity: 0.25 }}>
              <AddIcon className="size-6" pathClassName="stroke-teachLoop-textPrimary" />
            </Pressable>
          </View>
        </View>
      </View>
      <View className="h-9" />
      <View className="mx-5 flex-row items-center justify-between">
        {!!preSelectedTimeSlot?.slotId ? (
          <Button onPress={onRemove} borderLess intent="danger" size="sm">
            <Button.Text intent="danger" size="sm">
              Remove
            </Button.Text>
          </Button>
        ) : (
          <View className="flex-1" />
        )}

        <Button
          onPress={() => onDone(slotStartTime, slotEndTime)}
          intent="primary"
          size="sm"
          icon
        >
          <Button.Icon as={CheckIcon} pathClassName="stroke-white" />
          <Button.Text size="sm">Save time slot</Button.Text>
        </Button>
      </View>
      <View className="h-8" />
    </View>
  );
}

function AssignTimeSlotSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    AssignTimeSlotSheetPayload,
    AssignTimeSlotSheetResult
  >(SheetIds.ASSIGN_TIME_SLOT);
  const { payload }: { payload?: AssignTimeSlotSheetPayload } = sheet ?? {};
  const preSelectedTimeSlot: AssignTimeSlotSheetPayload["preSelectedTimeSlot"] =
    payload?.preSelectedTimeSlot;
  const day = payload?.day;
  const selectedSlots = day?.timeSlots ?? [];
  const timeSlots = preSelectedTimeSlot
    ? selectedSlots
        .filter(
          (slot) =>
            slot.startTime !== preSelectedTimeSlot.start &&
            slot.endTime !== preSelectedTimeSlot.end
        )
        .map((slot) => ({
          start: slot.startTime,
          end: slot.endTime,
        }))
    : selectedSlots.map((slot) => ({
        start: slot.startTime,
        end: slot.endTime,
      }));
  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;
  const isFocused = useIsFocused();

  useEffect(() => {
    if (isFocused) {
      setShouldRender(true);
    } else {
      setShouldRender(false);
    }
  }, [isFocused]);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(() => {
    close();
  }, [close]);

  const onDone = useCallback(
    (slotStartTime: string, slotEndTime: string) => {
      close({
        selectedTimeSlot: {
          start: slotStartTime,
          end: slotEndTime,
        },
        slotId: preSelectedTimeSlot?.slotId,
      });
    },
    [close, preSelectedTimeSlot]
  );

  const onRemove = useCallback(() => {
    close({
      slotId: preSelectedTimeSlot?.slotId,
    });
  }, [close, preSelectedTimeSlot]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      style={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetView>
        <AssignTimeSlotSheetContent
          timeSlots={timeSlots}
          onClose={onClose}
          onDone={onDone}
          onRemove={onRemove}
          preSelectedTimeSlot={preSelectedTimeSlot}
        />
      </BottomSheetView>
    </BottomSheet>
  ) : null;
}

export default AssignTimeSlotSheet;
